import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  User
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { getIdToken, getIdTokenForced, storeToken, clearStoredToken, getStoredToken, TokenInfo } from '../utils/tokenUtils';
import { UserProfile } from '../services/userService';
import { config } from '../config';

interface AuthState {
  user: User | null;
  mongoUser: UserProfile | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  token: string | null;
  tokenInfo: TokenInfo | null;
  tokenLoading: boolean;
  isMongoVerified: boolean;
  orgId: string | null;
  // Tutorial state - tracks active step index (null when not running)
  tutorialActiveStepIndex: number | null;
}

const initialState: AuthState = {
  user: null,
  mongoUser: null,
  loading: false,
  error: null,
  isAuthenticated: false,
  token: null,
  tokenInfo: null,
  tokenLoading: false,
  isMongoVerified: false,
  orgId: null,
  tutorialActiveStepIndex: null,
};

const FIREBASE_AUTH_ERROR_MESSAGES: Record<string, string> = {
  'auth/invalid-credential':        'Incorrect email or password. Please try again.',
  'auth/invalid-email':             'Please enter a valid email address.',
  'auth/user-disabled':             'This account has been disabled. Please contact support.',
  'auth/user-not-found':            'No account found with this email address.',
  'auth/wrong-password':            'Incorrect password. Please try again.',
  'auth/too-many-requests':         'Too many failed attempts. Please wait a moment or reset your password.',
  'auth/network-request-failed':    'Network error. Please check your connection and try again.',
  'auth/operation-not-allowed':     'This sign-in method is not enabled. Please contact support.',
  'auth/popup-closed-by-user':      'Sign-in was cancelled. Please try again.',
  'auth/requires-recent-login':     'Please sign in again to continue.',
  'auth/account-exists-with-different-credential': 'An account already exists with a different sign-in method.',
};

const getFirebaseAuthErrorMessage = (error: any): string => {
  if (error?.code && FIREBASE_AUTH_ERROR_MESSAGES[error.code]) {
    return FIREBASE_AUTH_ERROR_MESSAGES[error.code];
  }
  return 'Sign in failed. Please try again.';
};

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    try {
      console.debug('[AuthSlice] Starting login process for:', email);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Get JWT token after successful login
      console.debug('[AuthSlice] Login successful, getting JWT token...');
      const tokenInfo = await getIdToken();

      if (tokenInfo) {
        storeToken(tokenInfo);
        console.debug('[AuthSlice] JWT token obtained and stored successfully');
      } else {
        console.warn('[AuthSlice] Failed to get JWT token after login');
      }

      return {
        user: userCredential.user,
        tokenInfo
      };
    } catch (error: any) {
      console.error('[AuthSlice] Login failed:', error);
      return rejectWithValue(getFirebaseAuthErrorMessage(error));
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/registerUser',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    try {
      console.debug('[AuthSlice] Starting registration process for:', email);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Get JWT token after successful registration
      console.debug('[AuthSlice] Registration successful, getting JWT token...');
      const tokenInfo = await getIdToken();
      
      if (tokenInfo) {
        storeToken(tokenInfo);
        console.debug('[AuthSlice] JWT token obtained and stored successfully');
      } else {
        console.warn('[AuthSlice] Failed to get JWT token after registration');
      }
      
      return {
        user: userCredential.user,
        tokenInfo
      };
    } catch (error: any) {
      console.error('[AuthSlice] Registration failed:', error);
      return rejectWithValue(getFirebaseAuthErrorMessage(error));
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { rejectWithValue }) => {
    try {
      console.debug('[AuthSlice] Starting logout process...');
      await signOut(auth);
      clearStoredToken();
      console.debug('[AuthSlice] Logout successful, tokens cleared');
      return null;
    } catch (error: any) {
      console.error('[AuthSlice] Logout failed:', error);
      // Still clear tokens even if Firebase signOut fails
      clearStoredToken();
      return rejectWithValue(error.message);
    }
  }
);

// New thunk for refreshing token
export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      console.debug('[AuthSlice] Refreshing JWT token...');
      const tokenInfo = await getIdTokenForced();

      if (tokenInfo) {
        storeToken(tokenInfo);
        console.debug('[AuthSlice] Token refreshed successfully');
        return tokenInfo;
      } else {
        console.warn('[AuthSlice] Failed to refresh token');
        return rejectWithValue('Failed to refresh token');
      }
    } catch (error: any) {
      console.error('[AuthSlice] Token refresh failed:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Verify MongoDB admin portal access (checks for admin role or default flag)
export const fetchMongoUser = createAsyncThunk(
  'auth/fetchMongoUser',
  async (_, { rejectWithValue }) => {
    try {
      console.debug('[AuthSlice] Verifying admin portal access...');

      // Get auth token
      const user = auth.currentUser;
      if (!user) {
        return rejectWithValue('User not authenticated');
      }
      const token = await user.getIdToken();

      // Use admin portal verification endpoint (checks admin role or default flag)
      const response = await fetch(`${config.api.baseURL}/api/admin/verify-admin-access`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorCode = errorData.error || 'USER_NOT_AUTHORIZED';
        const errorMessage = errorData.message || 'User not authorized for admin portal access';

        console.warn('[AuthSlice] Admin portal access verification failed:', errorCode);

        // Map error codes to user-friendly messages
        let userMessage = errorMessage;
        switch (errorCode) {
          case 'USER_DISABLED':
            userMessage = 'Your account has been disabled. Please contact support.';
            break;
          case 'USER_NOT_REGISTERED':
            userMessage = 'Your account is not registered in the system. Please contact your administrator.';
            break;
          case 'NOT_ADMIN':
            userMessage = 'Access denied. Admin role or default flag required.';
            break;
          case 'USER_NOT_AUTHORIZED':
          default:
            userMessage = 'You are not authorized to access the admin portal. Please contact your administrator.';
            break;
        }

        return rejectWithValue(userMessage);
      }

      const data = await response.json();
      if (data.success && data.data?.user) {
        console.debug('[AuthSlice] User access verified:', data.data.user.email);
        console.debug('[AuthSlice] User role:', data.data.user.role);
        console.debug('[AuthSlice] Organization ID:', data.data.org_id);

        return {
          user: data.data.user as UserProfile,
          orgId: data.data.org_id
        };
      } else {
        console.warn('[AuthSlice] Failed to verify user access');
        return rejectWithValue('Failed to verify user access');
      }
    } catch (error: any) {
      console.error('[AuthSlice] Verify user access failed:', error);
      return rejectWithValue(error.message || 'Failed to verify user access');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User | null>) => {
      console.debug('[AuthSlice] Setting user:', action.payload?.email || 'null');
      state.user = action.payload;

      // Only set isAuthenticated if we have user, valid token, AND MongoDB verification
      if (action.payload && state.tokenInfo) {
        // Check if token is not expired
        const now = Date.now();
        const bufferTime = 5 * 60 * 1000; // 5 minutes buffer
        const tokenValid = now < (state.tokenInfo.expiresAt - bufferTime);

        // User must be MongoDB verified to be authenticated
        state.isAuthenticated = tokenValid && state.isMongoVerified;

        if (!tokenValid) {
          console.warn('[AuthSlice] Token expired, user not authenticated');
          state.token = null;
          state.tokenInfo = null;
          clearStoredToken();
        }
      } else {
        state.isAuthenticated = false;
      }

      // If user is null, clear all auth data
      if (!action.payload) {
        state.token = null;
        state.tokenInfo = null;
        state.isAuthenticated = false;
        state.isMongoVerified = false;
        state.mongoUser = null;
        state.orgId = null;
        clearStoredToken();
      }
    },
    clearError: (state) => {
      state.error = null;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    setMongoUser: (state, action: PayloadAction<UserProfile | null>) => {
      console.debug('[AuthSlice] Setting MongoDB user:', action.payload?.email || 'null');
      state.mongoUser = action.payload;
    },
    setToken: (state, action: PayloadAction<TokenInfo | null>) => {
      if (action.payload) {
        console.debug('[AuthSlice] Setting token in state:', {
          expiresAt: new Date(action.payload.expiresAt).toISOString(),
          tokenLength: action.payload.token.length
        });
        state.token = action.payload.token;
        state.tokenInfo = action.payload;
      } else {
        console.debug('[AuthSlice] Clearing token from state');
        state.token = null;
        state.tokenInfo = null;
      }
    },
    initializeStoredToken: (state) => {
      // Only initialize if we don't already have a token in state
      if (state.token || state.tokenInfo) {
        console.debug('[AuthSlice] Token already in state, skipping initialization');
        return;
      }
      
      const storedToken = getStoredToken();
      if (storedToken) {
        console.debug('[AuthSlice] Initializing stored token:', {
          expiresAt: new Date(storedToken.expiresAt).toISOString(),
          tokenLength: storedToken.token.length
        });
        state.token = storedToken.token;
        state.tokenInfo = storedToken;
      } else {
        console.debug('[AuthSlice] No valid stored token found');
      }
    },
    validateAuth: (state) => {
      // Validate current authentication state
      if (!state.user || !state.tokenInfo || !state.isMongoVerified) {
        console.debug('[AuthSlice] Missing requirements for auth:', {
          hasUser: !!state.user,
          hasToken: !!state.tokenInfo,
          isMongoVerified: state.isMongoVerified
        });
        state.isAuthenticated = false;
        return;
      }

      // Check if token is expired
      const now = Date.now();
      const bufferTime = 5 * 60 * 1000; // 5 minutes buffer
      const tokenValid = now < (state.tokenInfo.expiresAt - bufferTime);

      if (!tokenValid) {
        console.warn('[AuthSlice] Token expired during validation');
        state.isAuthenticated = false;
        state.token = null;
        state.tokenInfo = null;
        clearStoredToken();
      } else {
        state.isAuthenticated = true;
      }
    },
    setMongoVerified: (state, action: PayloadAction<boolean>) => {
      console.debug('[AuthSlice] Setting MongoDB verification status:', action.payload);
      state.isMongoVerified = action.payload;

      // Update authentication status if we now have all requirements
      if (state.user && state.tokenInfo && action.payload) {
        const now = Date.now();
        const bufferTime = 5 * 60 * 1000;
        const tokenValid = now < (state.tokenInfo.expiresAt - bufferTime);
        state.isAuthenticated = tokenValid;
      }
    },
    setTutorialActiveStepIndex: (state, action: PayloadAction<number | null>) => {
      console.debug('[AuthSlice] Setting tutorial active step index:', action.payload);
      state.tutorialActiveStepIndex = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.error = null;
        if (action.payload.tokenInfo) {
          state.token = action.payload.tokenInfo.token;
          state.tokenInfo = action.payload.tokenInfo;
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.error = null;
        if (action.payload.tokenInfo) {
          state.token = action.payload.tokenInfo.token;
          state.tokenInfo = action.payload.tokenInfo;
        }
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.mongoUser = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.error = null;
        state.token = null;
        state.tokenInfo = null;
        state.isMongoVerified = false;
        state.orgId = null;
      })
      // Token refresh cases
      .addCase(refreshToken.pending, (state) => {
        state.tokenLoading = true;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.tokenLoading = false;
        state.token = action.payload.token;
        state.tokenInfo = action.payload;
      })
      .addCase(refreshToken.rejected, (state) => {
        state.tokenLoading = false;
        // Don't clear tokens on refresh failure, let them expire naturally
      })
      // Fetch MongoDB user cases
      .addCase(fetchMongoUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchMongoUser.fulfilled, (state, action) => {
        state.loading = false;
        state.mongoUser = action.payload.user;
        state.orgId = action.payload.orgId;
        state.isMongoVerified = true;

        // Check if we can now authenticate
        if (state.user && state.tokenInfo) {
          state.isAuthenticated = true;
        }
      })
      .addCase(fetchMongoUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isMongoVerified = false;
        state.mongoUser = null;
        state.isAuthenticated = false;
      });
  },
});

export const { setUser, clearError, setError, setMongoUser, setToken, initializeStoredToken, validateAuth, setMongoVerified, setTutorialActiveStepIndex } = authSlice.actions;
export default authSlice.reducer;